// Copyright (c) 2025 Audere, All Rights Reserved

import { Box, Button, Card, Divider, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";

import TestItem from "./TestItem";
import type { NewTestsProps } from "../types/projects";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { theme } from "../../theme";

import labsUrl from "../../assets/labs.svg";
import { useCallback } from "react";
import { useAppDispatch } from "../features/store";
import { clearNewTestFormValues } from "../features/data/projectSlice";

const NewTests = (props: NewTestsProps) => {
  const { newTests, selectedTestIds, onAddNewTest, onToggleNewTestSelection } = props;
  const { t } = useTranslation("NewTests");
  const isMobileView = useMediaQuery("(max-width:500px)");
  const dispatch = useAppDispatch();

  const handleNewTestSelection = useCallback(
    (testId: string, checked: boolean) => {
      let newSelected: string[];
      if (checked) {
        newSelected = Array.from(new Set([...selectedTestIds, testId]));
      } else {
        newSelected = selectedTestIds.filter(id => id !== testId);
      }
      onToggleNewTestSelection(newSelected);
    },
    [selectedTestIds, onToggleNewTestSelection]
  );
  const handleAddNewTest = () => {
    dispatch(clearNewTestFormValues());
    onAddNewTest();
  };

  return (
    <Box sx={styles.newTestsContainer}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack sx={styles.newTestsHeader}>
          <Box component="img" src={labsUrl} sx={styles.newTestLabsImg} alt="New test labs" />
          <Stack>
            <Typography variant="body1bold">{t("yourTests")}</Typography>
            <Typography variant="body2">{t("newTestInfo")}</Typography>
          </Stack>
        </Stack>
        {newTests.length > 0 && (
          <Button variant="outlined" sx={styles.addNewTestBtn} onClick={handleAddNewTest}>
            {t("addNewTest", { prefix: "+ " })}
          </Button>
        )}
      </Stack>
      <Divider />
      <Box p={2}>
        <Grid container direction="row" rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {newTests?.map((testItem, index) => (
            <Grid
              key={index}
              size={{ xs: isMobileView ? 12 : 6, sm: 4 }}
              sx={styles.testItemContainer}
            >
              <TestItem
                testItem={testItem}
                toggleTestSelection={(checked: boolean) =>
                  handleNewTestSelection(testItem.rdt_uid, checked)
                }
                isTestSelected={selectedTestIds.includes(testItem.rdt_uid)}
              />
            </Grid>
          ))}
          {newTests.length === 0 && (
            <Grid size={{ xs: isMobileView ? 12 : 6, sm: 4 }} sx={styles.testItemContainer}>
              <Card sx={styles.addNewTestCard} onClick={handleAddNewTest}>
                <AddIcon color="primary" fontSize="large" />
                <Typography color="primary" variant="body2bold" sx={globalStyles.gapTop}>
                  {t("addNewTest", { prefix: "" })}
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default NewTests;

const styles = createStyleSheet({
  newTestsContainer: {
    background: theme.palette.common.white,
    borderRadius: "6px",
  },
  newTestsHeader: {
    p: 1,
    px: 2,
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    gap: 2,
  },
  newTestLabsImg: { height: theme.spacing(4), objectFit: "contain" },
  addNewTestCard: {
    maxWidth: theme.spacing(28),
    width: "100%",
    height: theme.spacing(35),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "none",
  },
  testItemContainer: {
    ...globalStyles.gapTop,
    display: "flex",
    justifyContent: "center",
    minWidth: "175px",
  },
  addNewTestBtn: {
    px: 4,
    mr: 2,
  },
});
